import { useControllerConnectionStatus } from '../Hooks/useControllerConnectionStatus';
import { useEffect, useState } from 'react';

import Typography from '@material-ui/core/Typography';

import Button from '@material-ui/core/Button';

import { DatePicker, MyTimePicker } from '../FormWidgets/DatePicker.js';
import Desc from '../FormWidgets/Description';
import { SettingGroup, ValueBox } from '../FormWidgets/SettingGroup';
import { EventGapSelect } from '../FormWidgets/Select.js';
import { LoadSettingsDialog } from '../Utils/load_settings';
import { SettingActions } from '../components/index.js';

import _ from 'lodash';
import { addZ } from '../Utils/utils.js';

import Select from '@material-ui/core/Select';
import MenuItem from '@material-ui/core/MenuItem';
import Box from '@material-ui/core/Box';

import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';

import { useTabSettings } from './settingFunctions';
import { subscribeKey } from 'valtio/utils';
import { useSnapshot, subscribe } from 'valtio';
import { useTranslation } from 'react-i18next';

function makeTimeIndustrialPro(data, seconds = false) {
  let str = `${addZ(data.hour)}:${addZ(data.minute)}`;
  if (seconds) {
    str += `:${addZ(data.second)}`;
  }
  return str;
}

function makeJsonTime(seconds = true) {
  let t = {
    hour: 0,
    minute: 0
  };
  if (seconds) {
    t.second = 0;
  }

  return t;
}

function handleDateTimeInputIndustrialPro(name, value, controls, seconds = false) {
  let d = [
    [`${name}.hour`, `${name}.minute`],
    [value.getHours(), value.getMinutes()]
  ];

  if (seconds) {
    d[0].push(`${name}.second`);
    d[1].push(value.getSeconds());
  }
  controls.setValue(d[0], d[1]);
}

// @param key: 'event_timer' or 'interval_timer'
function removeTimer(key, id, values, controls) {
  let timer = [...values[key]].map((t) => (t.id == id ? { ...t, __deleted__: true } : t));

  controls.setValue(key, timer);
}

function handleIntInput(name, value, values, setValues, controls) {
  let _values = { ...values };
  let v = Number(value);
  _.set(_values, name, v);

  controls.saveTempSettings(name, v);

  setValues(_values);
}

export default function TimingSettings({ ...other }) {
  //const { t, params, stateManager, controller, parent, values, setValues, controls, openLoadSettingsDialog, closeLoadSettingsDialog, tab_id } = useTabSettings (null, true, ['time_cont_hour', 'time_cont_min', 'time_cont_sec']);
  const {
    t,
    params,
    stateManager,
    controller,
    parent,
    values,
    setValues,
    controls,
    openLoadSettingsDialog,
    closeLoadSettingsDialog,
    tab_id
  } = useTabSettings(null, [
    'time_cont_hour',
    'time_cont_sec',
    'time_cont_min',
    'time_cont_year',
    'time_cont_month',
    'time_cont_day'
  ]);
  const { status: connectionStatus, disabledReason: connectionDisabledReason } =
    useControllerConnectionStatus(controller);

  //const infos = useSnapshot (parent.infos);

  const write_access = controller.access_rights.timing.write;
  const [dateInputValid, setDateInputValid] = useState(true);
  const [dateResetKey, setDateResetKey] = useState(0);
  const reset = () => {
    controls.resetSettings();
    setDateResetKey((key) => key + 1);
  };
  const transform_sent_settings = (settings) => {
    if (!('_date_changed' in settings) || !settings['_date_changed']) {
      settings['time_cont_year'] = 0xffff;
      settings['time_cont_month'] = 0xff;
      settings['time_cont_day'] = 0xff;
    }
    if (!('_time_changed' in settings) || !settings['_time_changed']) {
      settings['time_cont_hour'] = 0xffff;
      settings['time_cont_min'] = 0xff;
      settings['time_cont_sec'] = 0xff;
    }
    return settings;
  };

  controls.restoreTempSettings = () => {
    let _values = _.merge(
      _.cloneDeep(parent.settings[tab_id]),
      _.cloneDeep(parent.temp_settings[tab_id])
    );
    for (const [k, v] of Object.entries(_values)) {
      if (v === '_deleted_') {
        delete _values[k];
      }
    }
    setValues(_values);
  };

  const handleDateTimeInput = (name, format, value) => {
    if (!write_access) return;
    let _values = { ...values };
    let fields;

    if (format === 'date') {
      fields = [
        ['year', value.getFullYear()],
        ['month', value.getMonth() + 1],
        ['day', value.getDate()]
      ];
    } else {
      fields = [
        ['hour', value.getHours()],
        ['min', value.getMinutes()]
      ];

      // TODO: remove this block when we switched to a more uniform naming of the timer keys
      if (name.includes('time_interval_2')) {
        fields = [
          ['hour', value.getHours()],
          ['minute', value.getMinutes()]
        ];
      }

      if (format === 'time_sec') {
        fields.push(['sec', value.getSeconds()]);
      }
    }

    for (let field of fields) {
      let v = Number(field[1]);
      _values[`${name}_${field[0]}`] = v;
      controls.saveTempSettings(`${name}_${field[0]}`, v);
    }

    // set whether controller date or time was changed
    if (name === 'time_cont' && format === 'date') {
      _values['_date_changed'] = true;
      controls.saveTempSettings('_date_changed', true);
    }
    if (name === 'time_cont' && format === 'time_sec') {
      _values['_time_changed'] = true;
      controls.saveTempSettings('_time_changed', true);
    }

    setValues(_values);
  };

  /*
   * pack single values for hour, min, sec into one time string
   */
  function makeTime(prefix, seconds = false) {
    let str = `${addZ(values[`${prefix}_hour`])}:${addZ(values[`${prefix}_min`])}`;

    // TODO: remove this block when we switched to a more uniform naming of the timer keys
    if (prefix.includes('time_interval_2')) {
      str = `${addZ(values[`${prefix}_hour`])}:${addZ(values[`${prefix}_minute`])}`;
    }

    if (seconds) {
      str += `:${addZ(values[`${prefix}_sec`])}`;
    }

    return str;
  }

  /*
   * Methods for manipulating dynamic event/interval timers (industrial only)
   */

  let static_event_timers = [5055, 5155].includes(controller.type)
    ? [1, 2, 3, 4, 5, 6, 7, 8]
    : [1, 2, 3, 4];
  let static_interval_timers = [5055, 5155].includes(controller.type)
    ? ['interval', 'time_interval_2']
    : ['interval'];

  const addEventTimer = function () {
    let timer = [...values.event_timer];
    timer.push({
      id: getNextFreeId(values.event_timer),
      cycles: [
        {
          start: makeJsonTime(false),
          duration: makeJsonTime()
        }
      ],
      exec_gap: 0
    });

    controls.setValue(`event_timer`, timer);
  };

  // pass array of event- or interval-timer
  const getNextFreeId = (timer) => {
    for (let i = 0; ; i++) {
      if (timer.filter((t) => t.id == i).length == 0) {
        return i;
      }
    }
  };

  const addIntervalTimer = () => {
    let timer = [...values.interval_timer];
    timer.push({
      id: getNextFreeId(values.interval_timer),
      type: 0,
      on: makeJsonTime(),
      off: makeJsonTime()
    });
    controls.setValue(`interval_timer`, timer);
  };

  const save = () => {
    if (!write_access || !dateInputValid) return;
    controls.saveSettings(transform_sent_settings);
    if (controller.type === 6000) {
      let _values = _.cloneDeep(values);
      _values.interval_timer = _values.interval_timer.filter((t) => t.__deleted__ !== true);
      _values.event_timer = _values.event_timer.filter((t) => t.__deleted__ !== true);
      parent.settings.timing = _values;
      setValues(_values);
    }
  };

  useEffect(() => {
    // Get current data and time from info values
    const time = parent.infos['time'].split(':');
    const date = parent.infos['date'].split('.');
    // Update tab-local data and time data
    let _values = { ...values };
    _values.controller_time = parent.infos['time'];
    _values.time_cont_hour = parseInt(time[0]);
    _values.time_cont_min = parseInt(time[1]);
    _values.time_cont_sec = parseInt(time[2]);
    _values.time_cont_year = parseInt(date[2]);
    _values.time_cont_month = parseInt(date[1]);
    _values.time_cont_day = parseInt(date[0]);
    setValues(_values);
    // Update controllers' data and time settings
    parent.settings.timing.controller_time = time;
    parent.settings.timing.time_cont_hour = parseInt(time[0]);
    parent.settings.timing.time_cont_min = parseInt(time[1]);
    parent.settings.timing.time_cont_sec = parseInt(time[2]);
    parent.settings.timing.controller_date = date[2] + '-' + date[1] + '-' + date[0];
    parent.settings.timing.time_cont_year = parseInt(date[2]);
    parent.settings.timing.time_cont_month = parseInt(date[1]);
    parent.settings.timing.time_cont_day = parseInt(date[0]);
  }, []);

  return (
    <>
      <LoadSettingsDialog
        open={openLoadSettingsDialog}
        handleClose={closeLoadSettingsDialog}
        controls={controls}
      />

      {write_access && <SettingActions
        title={connectionDisabledReason}
        onReset={reset}
        onSave={save}
        on_default={connectionStatus !== 'good' || !dateInputValid}
      />}

      <SettingGroup controller={controller} id={t('controller_time')}>
        <ValueBox controller={controller} id={'timing_controller_date'}>
          <Typography gutterBottom>
            <Desc id="controller_date" name="Controller Date" />
          </Typography>
          <DatePicker
            key={dateResetKey}
            value={`${values.time_cont_year}-${addZ(values.time_cont_month)}-${addZ(
              values.time_cont_day
            )}`}
            name="controller_date"
            inputProps={{ 'aria-label': t('controller_date') }}
            disabled={!write_access}
            onValidityChange={setDateInputValid}
            onChange={(date) => handleDateTimeInput('time_cont', 'date', date)}
          />
        </ValueBox>
        <ValueBox controller={controller} id={'timing_controller_time'}>
          <Typography gutterBottom>
            <Desc id="controller_time" name="Controller Time" />
          </Typography>
          <MyTimePicker
            value={makeTime('time_cont', true)}
            seconds={true}
            name="controller_time"
            id="controller_time"
            onChange={(value) => handleDateTimeInput('time_cont', 'time_sec', value)}
          />
        </ValueBox>
      </SettingGroup>

      <SettingGroup controller={controller} id={t('start_end_time')}>
        <ValueBox controller={controller} id={'timing_start_day'}>
          <Typography gutterBottom>
            <Desc id="start_day" name="Start Day at" />
          </Typography>
          <MyTimePicker
            value={makeTime('start_day')}
            seconds={false}
            name="start_day"
            onChange={(value) => handleDateTimeInput('start_day', 'time_min', value)}
          />
        </ValueBox>
        <ValueBox controller={controller} id={'timing_end_day'}>
          <Typography gutterBottom>
            <Desc id="end_day" name="End Day at" />
          </Typography>
          <MyTimePicker
            value={makeTime('end_day')}
            seconds={false}
            name="end_day"
            onChange={(value) => handleDateTimeInput('end_day', 'time_min', value)}
          />
        </ValueBox>
      </SettingGroup>

      {/* ------------ dynamic interval timer (industrial) --------------*/}
      {controller.type >= 6000 ? (
        <>
          {values.interval_timer.map((timer, e) => (
            <IntervalTimerIndustrialPro
              index={e}
              id={timer.id}
              values={values}
              setValues={setValues}
              controls={controls}
              controller={controller}
              write_access={write_access}
            />
          ))}
          <Button startIcon={<AddIcon />} color="primary" onClick={() => addIntervalTimer()}>
            {t('add_interval_timer')}
          </Button>
        </>
      ) : (
        <>
          {/* ------------ static interval timer (GrowBase) --------------*/}
          {static_interval_timers.map((e) => (
            <>
              {['day', 'night'].map((phase) => (
                <SettingGroup controller={controller} id={`${e}_${phase}`}>
                  {['on', 'off'].map((type) => (
                    <ValueBox>
                      <Typography gutterBottom>
                        <Desc id={`${e}_${type}_${phase}`} show={`${e}_${type}_${phase}`} />
                      </Typography>
                      <MyTimePicker
                        value={makeTime(`${e}_${type}_${phase}`, true)}
                        seconds={true}
                        name={`${e}_${type}_${phase}`}
                        onChange={(value) =>
                          handleDateTimeInput(`${e}_${type}_${phase}`, 'time_sec', value)
                        }
                      />
                    </ValueBox>
                  ))}
                </SettingGroup>
              ))}
            </>
          ))}
        </>
      )}

      {controller.type >= 6000 ? (
        <>
          {/* ------------ dynamic event timer (industrial) --------------*/}
          {values.event_timer.map((timer, e) => (
            <EventTimerIndustrialPro
              index={e}
              id={timer.id}
              controllerTimer={parent.settings.timing.event_timer.find(
                (savedTimer) => savedTimer.id === timer.id
              )}
              values={values}
              setValues={setValues}
              controls={controls}
            />
          ))}

          <Button startIcon={<AddIcon />} color="primary" onClick={() => addEventTimer()}>
            {t('add_event_timer')}
          </Button>
        </>
      ) : (
        <>
          {/* ------------ static event timer (growbase) --------------*/}
          {static_event_timers.map((e) => (
            <SettingGroup label={t(`event_${e}`)}>
              <ValueBox>
                <Typography gutterBottom>
                  <Desc id={`event${e}_start`} show="event_start" />
                </Typography>
                <MyTimePicker
                  value={makeTime(`event${e}_start`)}
                  seconds={false}
                  name={`event${e}_start`}
                  onChange={(value) => handleDateTimeInput(`event${e}_start`, 'time_min', value)}
                />
              </ValueBox>
              <ValueBox>
                <Typography gutterBottom>
                  <Desc id={`event${e}_duration`} show="event_duration" />
                </Typography>
                <MyTimePicker
                  value={makeTime(`event${e}_duration`, true)}
                  seconds={true}
                  name={`event${e}_duration`}
                  onChange={(value) => handleDateTimeInput(`event${e}_duration`, 'time_sec', value)}
                />
              </ValueBox>
              <ValueBox>
                <Typography gutterBottom>
                  <Desc id={`time_event${e}_exec_gap`} show="event_exec_gap" />
                </Typography>
                <EventGapSelect
                  value={values[`time_event${e}_exec_gap`]}
                  controller_value={parent.settings.timing[`time_event${e}_exec_gap`]}
                  onChange={(event, value) =>
                    handleIntInput(
                      `time_event${e}_exec_gap`,
                      event.target.value,
                      values,
                      setValues,
                      controls
                    )
                  }
                />
              </ValueBox>
            </SettingGroup>
          ))}
        </>
      )}

      {write_access && <SettingActions
        title={connectionDisabledReason}
        onReset={reset}
        onSave={save}
        on_default={connectionStatus !== 'good' || !dateInputValid}
      />}
    </>
  );
}

export const default_event_timer = { id: 0, cycles: [], exec_gap: 0 };
export const default_interval_timer = {
  id: 0,
  on: { hour: 0, minute: 0, second: 0 },
  off: { hour: 0, minute: 0, second: 0 },
  type: 0
};

export function EventTimerIndustrialPro({
  index,
  id,
  controllerTimer,
  values,
  setValues,
  controls,
  value_path_prefix = '',
  allowDelete = true
}) {
  let { t } = useTranslation();
  let t_path = `${value_path_prefix}event_timer[${index}]`;
  let timer = _.get(values, t_path, {});
  timer = { ...default_event_timer, ...timer };

  const addCycle = function (et) {
    let cycles = [..._.get(values, `${t_path}.cycles`, [])];
    cycles.push({
      start: makeJsonTime(false),
      duration: makeJsonTime()
    });
    controls.setValue(`${t_path}.cycles`, cycles);
  };

  const removeCycle = function (et) {
    let cycles = [..._.get(values, `${t_path}.cycles`, [])];
    cycles.pop();
    controls.setValue(`${t_path}.cycles`, cycles);
  };

  return (
    <SettingGroup label={`${t('event')} ${id + 1}`}>
      {timer.__deleted__ !== true ? (
        <>
          {_.get(values, `${t_path}.cycles`, []).map((cycle, c) => (
            <Box>
              <ValueBox>
                <Typography gutterBottom>
                  <Desc id={`event${index}_start${c}`} show="event_start" />
                </Typography>
                <MyTimePicker
                  value={makeTimeIndustrialPro(cycle.start)}
                  seconds={false}
                  name={`event${index}_start${c}`}
                  onChange={(value) =>
                    handleDateTimeInputIndustrialPro(
                      `${t_path}.cycles[${c}].start`,
                      value,
                      controls
                    )
                  }
                />
              </ValueBox>
              <ValueBox>
                <Typography gutterBottom>
                  <Desc id={`event${index}_duration${c}`} show="event_duration" />
                </Typography>
                <MyTimePicker
                  value={makeTimeIndustrialPro(cycle.duration, true)}
                  seconds={true}
                  name={`event_timer_${index}_${c}_duration`}
                  onChange={(value) =>
                    handleDateTimeInputIndustrialPro(
                      `${t_path}.cycles[${c}].duration`,
                      value,
                      controls,
                      true
                    )
                  }
                />
              </ValueBox>
            </Box>
          ))}

          <Box style={{ display: 'flex', flexDirection: 'column' }}>
            <Button startIcon={<AddIcon />} onClick={() => addCycle(index)}>
              {t('add_cycle')}
            </Button>
            {(timer.cycles ?? []).length > 1 && (
              <Button startIcon={<DeleteIcon />} onClick={() => removeCycle(index)}>
                {t('remove_cycle')}
              </Button>
            )}
          </Box>
          <ValueBox style={{ maxWidth: 'fit-content' }}>
            <Typography gutterBottom>
              <Desc id={`time_event${index}_exec_gap`} show="event_exec_gap" />
            </Typography>
            <EventGapSelect
              value={timer.exec_gap ?? 0}
              controller_value={controllerTimer?.exec_gap}
              onChange={(event, value) =>
                handleIntInput(
                  `${t_path}.exec_gap`,
                  event.target.value,
                  values,
                  setValues,
                  controls
                )
              }
            />
          </ValueBox>
          {allowDelete && (
            <Button
              startIcon={<DeleteIcon />}
              color="secondary"
              onClick={() => removeTimer('event_timer', timer.id, values, controls)}
            >
              {t('remove_event_timer')}
            </Button>
          )}
        </>
      ) : (
        <>
          {t('deleted')}
          <br />
          {t('click save to confirm')}
        </>
      )}
    </SettingGroup>
  );
}

export function IntervalTimerIndustrialPro({
  index,
  id,
  values,
  setValues,
  controls,
  controller,
  write_access,
  value_path_prefix = '',
  allowDelete = true
}) {
  let { t } = useTranslation();

  let t_path = `${value_path_prefix}interval_timer[${index}]`;
  let timer = _.get(values, t_path, {});
  timer = { ...default_interval_timer, ...timer };
  return (
    <SettingGroup label={`${t('interval')} ${id + 1}`}>
      {timer.__deleted__ !== true ? (
        <>
          <ValueBox controller={controller} id={'timing_interval_on'}>
            <Typography gutterBottom>
              <Desc id="interval_on" name="Interval On" />
            </Typography>
            <MyTimePicker
              value={makeTimeIndustrialPro(timer.on, true)}
              seconds={true}
              name="interval_on"
              onChange={(value) =>
                handleDateTimeInputIndustrialPro(`${t_path}.on`, value, controls, true)
              }
            />
          </ValueBox>
          <ValueBox>
            <Typography gutterBottom>
              <Desc id="interval_off" name="Interval Off" />
            </Typography>
            <MyTimePicker
              value={makeTimeIndustrialPro(timer.off, true)}
              seconds={true}
              name="interval_off"
              onChange={(value) =>
                handleDateTimeInputIndustrialPro(`${t_path}.off`, value, controls, true)
              }
            />
          </ValueBox>
          <ValueBox>
            <Typography gutterBottom>
              <Desc id="interval_phase" name="Phase" />
            </Typography>
            <Select
              value={timer.type}
              onChange={(evt) => controls.setValue(`${t_path}.type`, evt.target.value)}
            >
              <MenuItem value={0} selected={true}>
                {t('Day')}
              </MenuItem>
              <MenuItem value={1}>{t('Night')}</MenuItem>
              <MenuItem value={2}>{t('Day + Night')}</MenuItem>
            </Select>
          </ValueBox>

          <Button
            startIcon={<DeleteIcon />}
            color="secondary"
            onClick={() => removeTimer('interval_timer', id, values, controls)}
          >
            {t('remove_interval_timer')}
          </Button>
        </>
      ) : (
        <>
          {t('deleted')}
          <br />
          {t('click save to confirm')}
        </>
      )}
    </SettingGroup>
  );
}
